import { isEqual, isNil, keys, sortBy } from "lodash";
import type { nil } from "../type-utils";
import type {
  ProcessModelCopyExamTaskTypeName,
  ProcessModelCopyLanguageEducationFields,
  ProcessModelCopyTask,
} from "./process-model-copy.model";

// Each exam task type with the type of the partner it is paired with
const EXAM_PARTNER_TYPE_NAMES: Record<ProcessModelCopyExamTaskTypeName, ProcessModelCopyExamTaskTypeName> = {
  ProcessTaskEducationAssignToExamModel: "ProcessTaskEducationEnterExamResultModel",
  ProcessTaskEducationEnterExamResultModel: "ProcessTaskEducationAssignToExamModel",
};

const EXAM_TASK_TYPE_NAMES: readonly string[] = keys(EXAM_PARTNER_TYPE_NAMES);

/** Whether a task type is one of the exam tasks that pair up at runtime. */
export function isExamTaskTypeName(typeName: string | nil): typeName is ProcessModelCopyExamTaskTypeName {
  return EXAM_TASK_TYPE_NAMES.includes(typeName);
}

/**
 * Whether one of the tasks is the partner of an exam task: the other exam task type for the same exam type, language
 * level and exam modules (in any order), as `ProcessTaskService.getMatchingEducationTask` pairs them at runtime, where
 * nothing pairs without exam modules on both sides. A task that is no exam task has none.
 */
export function hasExamPartner(task: ProcessModelCopyTask, tasks: readonly ProcessModelCopyTask[]): boolean {
  const typeName = task._typeName;
  if (!isExamTaskTypeName(typeName)) {
    return false;
  }

  const partnerTypeName = EXAM_PARTNER_TYPE_NAMES[typeName];
  return tasks.some(
    (candidate) =>
      candidate._typeName === partnerTypeName &&
      isSameExam(task.languageEducationFields, candidate.languageEducationFields)
  );
}

function isSameExam(
  fields: ProcessModelCopyLanguageEducationFields | nil,
  otherFields: ProcessModelCopyLanguageEducationFields | nil
): boolean {
  if (isNil(fields?.examModules) || isNil(otherFields?.examModules)) {
    return false;
  }

  // A field missing in the stored JSON is null in a GraphQL result
  const isSameExamType = (fields.examType ?? null) === (otherFields.examType ?? null);
  const isSameLanguageLevel = (fields.languageLevel ?? null) === (otherFields.languageLevel ?? null);
  return isSameExamType && isSameLanguageLevel && isEqual(sortBy(fields.examModules), sortBy(otherFields.examModules));
}
